import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { LayoutGrid, Binary, Award, Clock, Cpu, Image as ImageIcon, Gamepad2, Activity, Send } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';

const nodes = [
    { name: 'HOME_CORE', path: '/', icon: <LayoutGrid />, desc: 'Primary entry point' },
    { name: 'VIRTUAL_BIO', path: '/aboutme', icon: <Binary />, desc: 'Neural personality profile' },
    { name: 'HISTORY_LOG', path: '/timeline', icon: <Clock />, desc: 'Temporal event sequence' },
    { name: 'SYS_SCHEMATICS', path: '/architecture', icon: <Cpu />, desc: 'Structural blueprints' },
    { name: 'HONOR_VAULT', path: '/achievements', icon: <Award />, desc: 'Cryptographic proofs' },
    { name: 'VISUAL_ARCHIVE', path: '/gallery', icon: <ImageIcon />, desc: 'Rendered assets' },
    { name: 'UI_PLAYGROUND', path: '/games', icon: <Gamepad2 />, desc: 'Stress-test modules' },
    { name: 'MISSION_CONTROL', path: '/dashboard', icon: <Activity />, desc: 'Ops monitoring' },
    { name: 'COMM_SIGNAL', path: '/contact', icon: <Send />, desc: 'Handshake protocol' },
];

export function Nexus() {
    return (
        <div className="pt-32 pb-48 px-6 md:px-12 max-w-7xl mx-auto min-h-screen flex flex-col items-center">
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center mb-24"
            >
                <div className="text-[10px] font-mono text-emerald-500 uppercase tracking-[1em] mb-4">Central Hub</div>
                <h1 className="text-7xl md:text-9xl font-black text-white tracking-tighter mb-8 italic">
                    THE <span className="text-emerald-500 underline decoration-wavy">NEXUS</span>
                </h1>
                <p className="text-gray-500 font-mono text-xs uppercase tracking-widest max-w-xl mx-auto leading-loose">
                    Intersection of all architectural branches and operational nodes. Choose your destination within the Lehana ecosystem.
                </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
                {nodes.map((node, idx) => (
                    <motion.div
                        key={node.name}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                    >
                        <Link to={node.path}>
                            <GlassCard className="p-8 border-white/5 hover:border-emerald-500/40 hover:bg-emerald-500/[0.02] transition-all duration-500 group h-full">
                                <div className="flex items-center justify-between mb-8">
                                    <div className="p-3 bg-white/5 rounded-xl text-gray-400 group-hover:text-emerald-400 group-hover:bg-emerald-500/10 transition-all duration-500">
                                        {node.icon}
                                    </div>
                                    <span className="text-[8px] font-mono text-gray-700 tracking-widest">PATH_0{idx + 1}</span>
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2 tracking-tighter group-hover:translate-x-2 transition-transform duration-300">
                                    {node.name}
                                </h3>
                                <p className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">{node.desc}</p>

                                <div className="mt-8 flex justify-end">
                                    <div className="w-8 h-px bg-white/10 group-hover:w-full group-hover:bg-emerald-500 transition-all duration-700" />
                                </div>
                            </GlassCard>
                        </Link>
                    </motion.div>
                ))}
            </div>

            {/* Nexus Core Status */}
            <div className="mt-32 p-8 border border-white/5 bg-black/40 rounded-full flex flex-wrap justify-center gap-12 text-[9px] font-mono text-gray-600 uppercase tracking-[0.3em]">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    NODES_ACTIVE: 09
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    LATENCY: 14MS
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    ENCRYPTION: AES-256
                </div>
            </div>
        </div>
    );
}
